import { cn } from "~/lib/utils";
import {
  type QuotedContextDraft,
  formatQuotedContextLabel,
  isQuotedContextExpired,
} from "~/lib/quotedContext";
import { QuotedContextInlineChip } from "./QuotedContextInlineChip";

interface ComposerPendingQuotedContextsProps {
  contexts: ReadonlyArray<QuotedContextDraft>;
  className?: string;
}

interface ComposerPendingQuotedContextChipProps {
  context: QuotedContextDraft;
}

export function ComposerPendingQuotedContextChip({
  context,
}: ComposerPendingQuotedContextChipProps) {
  const label = formatQuotedContextLabel(context);
  const expired = isQuotedContextExpired(context);
  // Only a terminal selection can reach this state: its text is deliberately not persisted, so a
  // reload leaves the chip pointing at a buffer nobody kept. A message quote keeps its text, and
  // an empty one is dropped on rehydration instead of shown.
  const tooltipText = expired
    ? `Terminal context expired. Remove and re-add ${label} to include it in your message.`
    : context.text;

  return (
    <QuotedContextInlineChip
      kind={context.kind}
      label={label}
      tooltipText={tooltipText}
      expired={expired}
    />
  );
}

export function ComposerPendingQuotedContexts(props: ComposerPendingQuotedContextsProps) {
  const { contexts, className } = props;

  if (contexts.length === 0) {
    return null;
  }

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {contexts.map((context) => (
        <ComposerPendingQuotedContextChip key={context.id} context={context} />
      ))}
    </div>
  );
}
